import {addDays, startOfToday} from 'date-fns';
import {Action, closeCurrentDialog, TabProps, Tabs} from 'platform/components';
import {match} from 'ts-pattern';

import {useGetMyPlannedTasksQuery, useGetMyTasksQuery, useGetTasksByMeQuery} from '@dms/api/core';
import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {EntityResourceIds, GetUserApiResponse} from '@dms/api/shared';
import {openCreateTaskFormDialog, openEditTaskFormDialog} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {employeeRoutes, taskManagerRoutes} from '@dms/routes';
import {refreshDmsDatagrid, useDataQuery, usePermissions} from '@dms/shared';

import {
  RequiredTestIdProps,
  buildArray,
  useNavigate,
  suffixTestId,
  composePath,
  getApiDateString,
} from 'shared/web';

import {useResourceIdentifier} from '../../../hooks/useResourceIdentifier';
import {TaskCenterList} from '../../TaskCenterList/TaskCenterList';

interface TaskCenterContentProps extends RequiredTestIdProps {
  onClick?: () => void;
  tabsVariant?: TabProps['variant'];
}

const TASK_CENTER_TAB = {
  MY_TASKS: 'my-tasks',
  PLANNED_TASKS: 'planned-tasks',
  BY_ME_TASKS: 'by-me-tasks',
} as const;

type TaskCenterTab = (typeof TASK_CENTER_TAB)[keyof typeof TASK_CENTER_TAB];

const getPlannedFromDate = () => getApiDateString(addDays(startOfToday(), 1));

export function TaskCenterContent(props: TaskCenterContentProps) {
  const [canCreateTask, canNavigateToEmployeeDetail] = usePermissions({
    permissionKeys: ['createTask', 'employeeRead'],
  });

  const {data: userInfo} = useGetCurrentUserInfoQuery();
  const userId = userInfo?.id ?? '';

  const {queryParams: assignedToMeQueryParams} = useDataQuery('task', {
    filters: {
      assignedTo: [userId],
    },
    order: [
      {
        columnKey: 'createdAt',
        order: 'DESC',
      },
    ],
  });

  const {queryParams: reportedByMeQueryParams} = useDataQuery('task', {
    filters: {
      reportedBy: [userId],
    },
    order: [
      {
        columnKey: 'createdAt',
        order: 'DESC',
      },
    ],
  });

  const {queryParams: plannedQueryParams} = useDataQuery('task', {
    filters: {
      assignedTo: [userId],
      dueDate: {from: getPlannedFromDate(), to: null},
    },
    order: [
      {
        columnKey: 'dueDate',
        order: 'ASC',
      },
    ],
  });

  const navigate = useNavigate();

  const {resourceId, resourceRecordId} = useResourceIdentifier();

  // Refetch on open: a notification arriving while the popup is closed cannot refresh an unsubscribed
  // list, so opening the popup must read the current state rather than a stale cache entry.
  const {
    data: myTasks,
    isLoading: areMyTasksLoading,
    isError: hasMyTasksError,
  } = useGetMyTasksQuery(undefined, {refetchOnMountOrArgChange: true});

  const {
    data: plannedTasks,
    isLoading: arePlannedTasksLoading,
    isError: hasPlannedTasksError,
  } = useGetMyPlannedTasksQuery(undefined, {refetchOnMountOrArgChange: true});

  const {
    data: tasksByMe,
    isLoading: areTasksByMeLoading,
    isError: hasTasksByMeError,
  } = useGetTasksByMeQuery(undefined, {refetchOnMountOrArgChange: true});

  const TabActions = buildArray<Action>().when(canCreateTask, {
    type: 'button',
    title: i18n.t('entity.task.actions.createTask'),
    variant: 'link',
    leftIcon: 'content/add_circle',
    onClick: () => {
      closeCurrentDialog();
      openCreateTaskFormDialog({
        'data-testid': 'task-center-create-task',
        relatedRecordId: resourceRecordId,
        resourceId,
        onCreate() {
          refreshDmsDatagrid('task');
          if (resourceId) {
            if (resourceId === EntityResourceIds.businessCase) {
              refreshDmsDatagrid('business-case-task');
            } else {
              refreshDmsDatagrid(`${resourceId.toLowerCase()}-task`);
            }
          }
        },
      });
      props.onClick?.();
    },
  });

  const handleShowAllClick = (tab: TaskCenterTab) => {
    const queryParams = match(tab)
      .with(TASK_CENTER_TAB.MY_TASKS, () => assignedToMeQueryParams)
      .with(TASK_CENTER_TAB.PLANNED_TASKS, () => plannedQueryParams)
      .with(TASK_CENTER_TAB.BY_ME_TASKS, () => reportedByMeQueryParams)
      .exhaustive();

    navigate(composePath(taskManagerRoutes.taskOverview, {queryParams}));
    props.onClick?.();
  };

  const handleAssigneeClick = (user: GetUserApiResponse) => {
    navigate(
      composePath(employeeRoutes.detail, {
        params: {id: user.id},
      })
    );
  };

  const tabs = buildArray<TabProps>()
    .add({
      id: TASK_CENTER_TAB.MY_TASKS,
      title: i18n.t('entity.task.labels.myTasks'),
      content: (
        <TaskCenterList
          data-testid={suffixTestId('myTasks', props)}
          list={myTasks ?? []}
          isLoading={areMyTasksLoading}
          isError={hasMyTasksError}
          onItemClick={(task) => {
            closeCurrentDialog();
            openEditTaskFormDialog({
              id: task.id,
              'data-testid': 'task-center-myTasks',
              relatedRecordId: resourceRecordId,
              onDelete() {
                refreshDmsDatagrid('task');
              },
              onEdit() {
                refreshDmsDatagrid('task');
              },
              resourceId,
            });
            props.onClick?.();
          }}
          shouldRenderShowAllButton
          hideAssignee
          onShowAllClick={() => handleShowAllClick(TASK_CENTER_TAB.MY_TASKS)}
        />
      ),
      actions: TabActions,
    })
    .add({
      id: TASK_CENTER_TAB.PLANNED_TASKS,
      title: i18n.t('entity.task.labels.plannedTasks'),
      content: (
        <TaskCenterList
          data-testid={suffixTestId('plannedTasks', props)}
          list={plannedTasks ?? []}
          isLoading={arePlannedTasksLoading}
          isError={hasPlannedTasksError}
          onItemClick={(task) => {
            closeCurrentDialog();
            openEditTaskFormDialog({
              id: task.id,
              'data-testid': 'task-center-plannedTasks',
              relatedRecordId: resourceRecordId,
              onDelete() {
                refreshDmsDatagrid('task');
              },
              onEdit() {
                refreshDmsDatagrid('task');
              },
              resourceId,
            });
            props.onClick?.();
          }}
          shouldRenderShowAllButton
          hideAssignee
          onShowAllClick={() => handleShowAllClick(TASK_CENTER_TAB.PLANNED_TASKS)}
        />
      ),
      actions: TabActions,
    })
    .add({
      id: TASK_CENTER_TAB.BY_ME_TASKS,
      title: i18n.t('entity.task.labels.byMeTasks'),
      content: (
        <TaskCenterList
          data-testid={suffixTestId('byMeTasks', props)}
          list={tasksByMe ?? []}
          isLoading={areTasksByMeLoading}
          isError={hasTasksByMeError}
          onItemClick={(task) => {
            closeCurrentDialog();
            openEditTaskFormDialog({
              id: task.id,
              'data-testid': 'task-center-byMeTasks',
              relatedRecordId: resourceRecordId,
              onDelete() {
                refreshDmsDatagrid('task');
              },
              onEdit() {
                refreshDmsDatagrid('task');
              },
            });
            props.onClick?.();
          }}
          shouldRenderShowAllButton
          onAssigneeClick={canNavigateToEmployeeDetail ? handleAssigneeClick : undefined}
          onShowAllClick={() => handleShowAllClick(TASK_CENTER_TAB.BY_ME_TASKS)}
        />
      ),
      actions: TabActions,
    });

  return <Tabs variant={props.tabsVariant} isFullHeight tabs={tabs} />;
}
